/**
 * ─────────────────────────────────────────────
 * src/store/slices/kanbanSlice.js
 * ─────────────────────────────────────────────
 *
 * Manages all kanban board state:
 *   - kanban project list (landing page)
 *   - board columns (lists + tasks)
 *   - selected project
 *   - all task operations (create, edit, delete, toggle)
 *   - subtask operations (add, toggle)
 *   - comment operations (post)
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import kanbanService from "../../services/kanbanService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// ─────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────

export const fetchKanbanProjects = createAsyncThunk(
  "kanban/fetchKanbanProjects",
  async (_, { rejectWithValue }) => {
    try {
      const data = await kanbanService.getKanbanProjects();
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchKanbanBoard = createAsyncThunk(
  "kanban/fetchKanbanBoard",
  async (projectId, { rejectWithValue }) => {
    try {
      await kanbanService.getKanbanProjects();
      // Board is fetched via projectService (same endpoint as projects lists)
      // We import projectService here to avoid circular deps
      const { default: projectService } =
        await import("../../services/projectService");
      const boardData = await projectService.getProjectLists(projectId);
      return boardData.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const createKanbanList = createAsyncThunk(
  "kanban/createKanbanList",
  async ({ projectId, name }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.createList(projectId, name);
      return data.data; // new list object
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const createKanbanTask = createAsyncThunk(
  "kanban/createKanbanTask",
  async ({ projectId, listId, taskData }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.createTask(projectId, listId, taskData);
      return { listId, task: data.data };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const editKanbanTask = createAsyncThunk(
  "kanban/editKanbanTask",
  async ({ projectId, listId, taskId, taskData }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.editTask(
        projectId,
        listId,
        taskId,
        taskData,
      );
      return { listId, taskId, task: data.data };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const deleteKanbanTask = createAsyncThunk(
  "kanban/deleteKanbanTask",
  async ({ projectId, listId, taskId }, { rejectWithValue }) => {
    try {
      await kanbanService.deleteTask(projectId, listId, taskId);
      return { listId, taskId };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const toggleKanbanTaskStatus = createAsyncThunk(
  "kanban/toggleKanbanTaskStatus",
  async ({ projectId, listId, taskId }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.toggleTaskStatus(
        projectId,
        listId,
        taskId,
      );
      return { listId, taskId, task: data.data };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const addKanbanSubtask = createAsyncThunk(
  "kanban/addKanbanSubtask",
  async ({ projectId, listId, taskId, title }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.addSubtask(
        projectId,
        listId,
        taskId,
        title,
      );
      return { listId, taskId, subtask: data.data.subtask };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const toggleKanbanSubtask = createAsyncThunk(
  "kanban/toggleKanbanSubtask",
  async ({ projectId, listId, taskId, subtaskId }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.toggleSubtask(
        projectId,
        listId,
        taskId,
        subtaskId,
      );
      return { listId, taskId, subtaskId, subtask: data.data.subtask };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const updateKanbanSubtask = createAsyncThunk(
  "kanban/updateKanbanSubtask",
  async (
    { projectId, listId, taskId, subtaskId, title },
    { rejectWithValue },
  ) => {
    try {
      const data = await kanbanService.updateSubtask(
        projectId,
        listId,
        taskId,
        subtaskId,
        title,
      );

      return {
        listId,
        taskId,
        subtaskId,
        subtask: data.data,
      };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const deleteKanbanSubtask = createAsyncThunk(
  "kanban/deleteKanbanSubtask",
  async ({ projectId, listId, taskId, subtaskId }, { rejectWithValue }) => {
    try {
      await kanbanService.deleteSubtask(projectId, listId, taskId, subtaskId);

      return {
        listId,
        taskId,
        subtaskId,
      };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchProjectLogs = createAsyncThunk(
  "kanban/fetchProjectLogs",
  async ({ projectId, page = 1 }, { rejectWithValue }) => {
    try {
      const { default: projectService } =
        await import("../../services/projectService");
      const data = await projectService.getProjectLogs(projectId, page);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const deleteProjectLogs = createAsyncThunk(
  "kanban/deleteProjectLogs",
  async (projectId, { rejectWithValue }) => {
    try {
      const { default: projectService } =
        await import("../../services/projectService");
      const data = await projectService.deleteProjectLogs(projectId);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const postKanbanComment = createAsyncThunk(
  "kanban/postKanbanComment",
  async ({ projectId, listId, taskId, payload }, { rejectWithValue }) => {
    try {
      const data = await kanbanService.postComment(
        projectId,
        listId,
        taskId,
        payload,
      );
      return { listId, taskId, comment: data.data };
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────
const initialState = {
  // Project list shown on kanban landing page
  projects: [],

  // Currently selected project
  selectedProject: null,

  // Board columns: [{ _id, name, tasks: [...] }]
  columns: [],

  // Loading flags
  isProjectsLoading: false,
  isBoardLoading: false,
  isCreatingList: false,
  isCreatingTask: false,
  isEditingTask: false,
  isDeletingTask: false,
  isTogglingStatus: false,
  isAddingSubtask: false,
  isTogglingSubtask: false,
  isPostingComment: false,

  // Activity logs (project-scoped, paginated 20/page — server-enforced)
  logs: [],
  logsProject: null,
  logsPagination: null,
  isLogsLoading: false,
  isDeletingLogs: false,

  // Errors
  projectsError: null,
  boardError: null,
  taskError: null,
  commentError: null,
  logsError: null,
};

// ─────────────────────────────────────────────
// HELPER — find column and task indices
// ─────────────────────────────────────────────
const findColByListId = (columns, listId) =>
  columns.findIndex((c) => c._id === listId);

const findTaskIdx = (col, taskId) =>
  col.tasks.findIndex((t) => t._id === taskId);

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────
const kanbanSlice = createSlice({
  name: "kanban",
  initialState,

  reducers: {
    setKanbanSelectedProject: (state, action) => {
      state.selectedProject = action.payload;
    },

    clearKanbanBoard: (state) => {
      state.selectedProject = null;
      state.columns = [];
    },

    // Allow direct column updates (for drag-drop in future)
    setKanbanColumns: (state, action) => {
      state.columns = action.payload;
    },

    clearKanbanErrors: (state) => {
      state.projectsError = null;
      state.boardError = null;
      state.taskError = null;
      state.commentError = null;
      state.logsError = null;
    },

    clearProjectLogs: (state) => {
      state.logs = [];
      state.logsProject = null;
      state.logsPagination = null;
      state.logsError = null;
    },
  },

  extraReducers: (builder) => {
    // ── fetchKanbanProjects ───────────────────
    builder
      .addCase(fetchKanbanProjects.pending, (state) => {
        state.isProjectsLoading = true;
        state.projectsError = null;
      })
      .addCase(fetchKanbanProjects.fulfilled, (state, action) => {
        state.isProjectsLoading = false;
        state.projects = action.payload;
      })
      .addCase(fetchKanbanProjects.rejected, (state, action) => {
        state.isProjectsLoading = false;
        state.projectsError = action.payload;
      });

    // ── fetchKanbanBoard ──────────────────────
    builder
      .addCase(fetchKanbanBoard.pending, (state) => {
        state.isBoardLoading = true;
        state.boardError = null;
      })
      .addCase(fetchKanbanBoard.fulfilled, (state, action) => {
        state.isBoardLoading = false;
        state.columns = action.payload.map((list) => ({
          ...list,
          tasks: Array.isArray(list.tasks) ? list.tasks : [],
        }));
      })
      .addCase(fetchKanbanBoard.rejected, (state, action) => {
        state.isBoardLoading = false;
        state.boardError = action.payload;
      });

    // ── createKanbanList ──────────────────────
    builder
      .addCase(createKanbanList.pending, (state) => {
        state.isCreatingList = true;
      })
      .addCase(createKanbanList.fulfilled, (state, action) => {
        state.isCreatingList = false;
        state.columns = [...state.columns, { ...action.payload, tasks: [] }];
      })
      .addCase(createKanbanList.rejected, (state) => {
        state.isCreatingList = false;
      });

    // ── createKanbanTask ──────────────────────
    builder
      .addCase(createKanbanTask.pending, (state) => {
        state.isCreatingTask = true;
        state.taskError = null;
      })
      .addCase(createKanbanTask.fulfilled, (state, action) => {
        state.isCreatingTask = false;
        const { listId, task } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          state.columns[colIdx].tasks = [
            ...(state.columns[colIdx].tasks || []),
            task,
          ];
        }
      })
      .addCase(createKanbanTask.rejected, (state, action) => {
        state.isCreatingTask = false;
        state.taskError = action.payload;
      });

    // ── editKanbanTask ────────────────────────
    builder
      .addCase(editKanbanTask.pending, (state) => {
        state.isEditingTask = true;
      })
      .addCase(editKanbanTask.fulfilled, (state, action) => {
        state.isEditingTask = false;
        const { listId, taskId, task } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);
          if (taskIdx !== -1) {
            state.columns[colIdx].tasks[taskIdx] = {
              ...state.columns[colIdx].tasks[taskIdx],
              ...task,
            };
          }
        }
      })
      .addCase(editKanbanTask.rejected, (state) => {
        state.isEditingTask = false;
      });

    // ── deleteKanbanTask ──────────────────────
    builder
      .addCase(deleteKanbanTask.pending, (state) => {
        state.isDeletingTask = true;
      })
      .addCase(deleteKanbanTask.fulfilled, (state, action) => {
        state.isDeletingTask = false;
        const { listId, taskId } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          state.columns[colIdx].tasks = state.columns[colIdx].tasks.filter(
            (t) => t._id !== taskId,
          );
        }
      })
      .addCase(deleteKanbanTask.rejected, (state) => {
        state.isDeletingTask = false;
      });

    // ── toggleKanbanTaskStatus ────────────────
    builder
      .addCase(toggleKanbanTaskStatus.pending, (state) => {
        state.isTogglingStatus = true;
      })
      .addCase(toggleKanbanTaskStatus.fulfilled, (state, action) => {
        state.isTogglingStatus = false;
        const { listId, taskId, task } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);
          if (taskIdx !== -1) {
            state.columns[colIdx].tasks[taskIdx] = {
              ...state.columns[colIdx].tasks[taskIdx],
              ...task,
            };
          }
        }
      })
      .addCase(toggleKanbanTaskStatus.rejected, (state) => {
        state.isTogglingStatus = false;
      });

    // ── addKanbanSubtask ──────────────────────
    builder
      .addCase(addKanbanSubtask.pending, (state) => {
        state.isAddingSubtask = true;
      })
      .addCase(addKanbanSubtask.fulfilled, (state, action) => {
        state.isAddingSubtask = false;
        const { listId, taskId, subtask } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);
          if (taskIdx !== -1) {
            const task = state.columns[colIdx].tasks[taskIdx];
            task.subtasks = [...(task.subtasks || []), subtask];
          }
        }
      })
      .addCase(addKanbanSubtask.rejected, (state) => {
        state.isAddingSubtask = false;
      });

    // ── toggleKanbanSubtask ───────────────────
    builder
      .addCase(toggleKanbanSubtask.pending, (state) => {
        state.isTogglingSubtask = true;
      })
      .addCase(toggleKanbanSubtask.fulfilled, (state, action) => {
        state.isTogglingSubtask = false;
        const { listId, taskId, subtaskId, subtask } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);
          if (taskIdx !== -1) {
            const task = state.columns[colIdx].tasks[taskIdx];
            task.subtasks = (task.subtasks || []).map((s) =>
              s._id === subtaskId ? { ...s, ...subtask } : s,
            );
          }
        }
      })
      .addCase(toggleKanbanSubtask.rejected, (state) => {
        state.isTogglingSubtask = false;
      });

    // ── updateKanbanSubtask ───────────────────
    builder
      .addCase(updateKanbanSubtask.fulfilled, (state, action) => {
        const { listId, taskId, subtaskId, subtask } = action.payload;

        const colIdx = findColByListId(state.columns, listId);

        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);

          if (taskIdx !== -1) {
            const task = state.columns[colIdx].tasks[taskIdx];

            task.subtasks = task.subtasks.map((s) =>
              s._id === subtaskId ? { ...s, ...subtask } : s,
            );
          }
        }
      })

      .addCase(deleteKanbanSubtask.fulfilled, (state, action) => {
        const { listId, taskId, subtaskId } = action.payload;

        const colIdx = findColByListId(state.columns, listId);

        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);

          if (taskIdx !== -1) {
            const task = state.columns[colIdx].tasks[taskIdx];

            task.subtasks = task.subtasks.filter((s) => s._id !== subtaskId);
          }
        }
      });

    // ── fetchProjectLogs ───────────────────────
    builder
      .addCase(fetchProjectLogs.pending, (state) => {
        state.isLogsLoading = true;
        state.logsError = null;
      })
      .addCase(fetchProjectLogs.fulfilled, (state, action) => {
        state.isLogsLoading = false;
        state.logs = action.payload.data;
        state.logsProject = action.payload.project;
        state.logsPagination = action.payload.pagination;
      })
      .addCase(fetchProjectLogs.rejected, (state, action) => {
        state.isLogsLoading = false;
        state.logsError = action.payload;
      });

    // ── deleteProjectLogs ──────────────────────
    builder
      .addCase(deleteProjectLogs.pending, (state) => {
        state.isDeletingLogs = true;
        state.logsError = null;
      })
      .addCase(deleteProjectLogs.fulfilled, (state) => {
        state.isDeletingLogs = false;
        state.logs = [];
        state.logsPagination = {
          currentPage: 1,
          totalPages: 1,
          total: 0,
          showing: { from: 0, to: 0, of: 0 },
        };
      })
      .addCase(deleteProjectLogs.rejected, (state, action) => {
        state.isDeletingLogs = false;
        state.logsError = action.payload;
      });

    // ── postKanbanComment ─────────────────────
    builder
      .addCase(postKanbanComment.pending, (state) => {
        state.isPostingComment = true;
        state.commentError = null;
      })
      .addCase(postKanbanComment.fulfilled, (state, action) => {
        state.isPostingComment = false;
        const { listId, taskId, comment } = action.payload;
        const colIdx = findColByListId(state.columns, listId);
        if (colIdx !== -1) {
          const taskIdx = findTaskIdx(state.columns[colIdx], taskId);
          if (taskIdx !== -1) {
            const task = state.columns[colIdx].tasks[taskIdx];
            task.comments = [...(task.comments || []), comment];
          }
        }
      })
      .addCase(postKanbanComment.rejected, (state, action) => {
        state.isPostingComment = false;
        state.commentError = action.payload;
      });
  },
});

export const {
  setKanbanSelectedProject,
  clearKanbanBoard,
  setKanbanColumns,
  clearKanbanErrors,
  clearProjectLogs,
} = kanbanSlice.actions;

export default kanbanSlice.reducer;
